import { ProductDetailPageTranslation } from "@swo/storefront";
import { PDPGiftCardTranslation } from "./types";
import {
  emailFormFieldDefinition,
  formDefinition,
  FormDefinition,
  FormDefinitionText,
  formIds,
} from "./from-definition";
import { useState } from "react";
import { GiftCartPersonalMessageT } from "@swo/storefront";

export type PDPGiftCardProps = {
  translations: PDPGiftCardTranslation;
  emailFieldDefinition: FormDefinitionText;
  messageFormDefinition: Array<FormDefinition>;
  formState: Map<string, string>;
  setFormFieldValue: (id: string) => (v: string) => void;
};

export type PDPGiftCardPropsWithPayload =
  | { giftCardProps: PDPGiftCardProps; payload: GiftCartPersonalMessageT }
  | undefined;

export function usePDPGiftCardProps(
  pdpTranslations: ProductDetailPageTranslation,
  shouldShowGiftCardForm: boolean,
): PDPGiftCardPropsWithPayload {
  const {
    giftCardFormEmailPlaceholder,
    giftCardFormFirstNamePlaceholder,
    giftCardFormLastNamePlaceholder,
    giftCardFormMessagePlaceholder,
    giftCardHeadline,
    giftCardMoreInfoLabel,
    giftCardParagraph,
    giftCardRecipientHeadline,
  } = pdpTranslations;

  const translations = {
    giftCardFormEmailPlaceholder,
    giftCardFormFirstNamePlaceholder,
    giftCardFormLastNamePlaceholder,
    giftCardFormMessagePlaceholder,
    giftCardHeadline,
    giftCardMoreInfoLabel,
    giftCardParagraph,
    giftCardRecipientHeadline,
  };

  const emailFieldDefinition = emailFormFieldDefinition(giftCardFormEmailPlaceholder.value);
  const messageFormDefinition = formDefinition(
    giftCardFormFirstNamePlaceholder.value,
    giftCardFormLastNamePlaceholder.value,
    giftCardFormMessagePlaceholder.value,
  );

  const [formState, setFormState] = useState(getInitialState);

  function getInitialState() {
    return new Map([...messageFormDefinition.map(f => [f.id, ""] as const), [formIds.email, ""]]);
  }

  const setFormFieldValue = (id: string) => (v: string) =>
    setFormState(m => {
      const next = new Map(m);

      next.set(id, v);

      return next;
    });

  if (!shouldShowGiftCardForm) {
    return undefined;
  }

  function toPayload(giftCardFormState: Map<string, string>): GiftCartPersonalMessageT {
    const firstName = giftCardFormState.get(formIds.firstName);
    const lastName = giftCardFormState.get(formIds.lastName);
    const email = giftCardFormState.get(formIds.email);
    const message = giftCardFormState.get(formIds.message);

    return {
      firstName,
      lastName,
      email,
      message,
    };
  }

  const giftCardProps = {
    translations,
    emailFieldDefinition,
    messageFormDefinition,
    formState,
    setFormFieldValue,
  };

  return {
    giftCardProps,
    payload: toPayload(giftCardProps.formState),
  };
}
